import type { FeedHealth, NowPlaying } from "./types.js";

/** The ordinary cadence: what a listener's client asks of a live feed. */
export const BASE_POLL_MS = 10_000;
/** Never faster than this, whatever the arithmetic says. */
export const MIN_POLL_MS = 3_000;
/** Where the backoff stops when the API is not answering. */
export const MAX_POLL_MS = 60_000;
/** A little after a track is due to end, so the next poll sees the new one. */
const SETTLE_MS = 500;

export interface PollInput {
  snapshot: NowPlaying | null;
  /** Consecutive failed reads. Zero on any success. */
  failures: number;
  now: number;
}

/**
 * When to ask again.
 *
 * A radio feed changes track at a knowable moment — the current track's start plus
 * its duration — so the poll that matters lands just after it rather than up to ten
 * seconds late. Everything else is the ordinary cadence, and a feed that is not
 * answering is backed off exponentially instead of being hammered while it is down.
 */
export function nextPollDelay({ snapshot, failures, now }: PollInput): number {
  if (failures > 0) {
    return Math.min(MAX_POLL_MS, BASE_POLL_MS * 2 ** (failures - 1));
  }
  const airing = snapshot?.nowPlaying;
  if (!airing || airing.durationMs <= 0) return BASE_POLL_MS;
  const startedAt = Date.parse(airing.startedAt);
  if (Number.isNaN(startedAt)) return BASE_POLL_MS;
  const remaining = startedAt + airing.durationMs - now;
  if (remaining > BASE_POLL_MS) return BASE_POLL_MS;
  // Due, or overdue: that feed should have moved on. Ask soon, never in a spin.
  return Math.max(MIN_POLL_MS, remaining + SETTLE_MS);
}

/** Never ask about health faster than this, whatever a short interval says. */
export const MIN_HEALTH_POLL_MS = 5_000;
/** Where the health cadence stops, so a feed checked hourly is still read hourly. */
export const MAX_HEALTH_POLL_MS = 60_000;
/**
 * Three reads per interval: often enough that a verdict reaches the screen well
 * inside the window it was established in, never so often that the client asks
 * faster than the product answers.
 */
const HEALTH_READS_PER_INTERVAL = 3;

/**
 * When to ask what the product has established about a feed.
 *
 * The answer only changes when the self-check runs, and how often that is, is the
 * feed's own `check_interval_seconds` — a per-feed setting, never a global one. So
 * the cadence is read from each feed's own policy rather than declared here: a feed
 * watched every ten seconds is read more often than a feed watched every ten
 * minutes, and neither number lives in this app.
 */
export function nextHealthPollDelay({
  snapshot,
  failures,
}: {
  snapshot: FeedHealth | null;
  failures: number;
  now?: number;
}): number {
  if (failures > 0) {
    return Math.min(MAX_HEALTH_POLL_MS, BASE_POLL_MS * 2 ** (failures - 1));
  }
  const interval = snapshot?.policy.checkIntervalSeconds;
  if (!interval || interval <= 0) return MAX_HEALTH_POLL_MS;
  return Math.min(
    MAX_HEALTH_POLL_MS,
    Math.max(
      MIN_HEALTH_POLL_MS,
      Math.round((interval * 1_000) / HEALTH_READS_PER_INTERVAL),
    ),
  );
}

/**
 * How far into the current track a feed is, as a fraction. The player shows this
 * as elapsed progress and never as a seek control — the stream is live, and there
 * is nowhere to seek to.
 */
export function airedFraction(
  snapshot: NowPlaying | null,
  now: number,
): number {
  const airing = snapshot?.nowPlaying;
  if (!airing || airing.durationMs <= 0) return 0;
  const startedAt = Date.parse(airing.startedAt);
  if (Number.isNaN(startedAt)) return 0;
  return Math.min(1, Math.max(0, (now - startedAt) / airing.durationMs));
}
